import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton
} from '@mui/material';
import { FileDownload as ExportIcon, PictureAsPdf as PdfIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { Currency } from '../utils/currency';
import { useNavigate } from 'react-router-dom';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';

function Reports() {
  const { user } = useAuth();
  const [reportType, setReportType] = useState('vat-summary');
  const [period, setPeriod] = useState('2024-01');
  const navigate = useNavigate();
  
  // Mock report data
  const vatSummaryData = {
    period: 'January 2024',
    totalSales: 15000.00,
    totalPurchases: 8000.00,
    vatOnSales: 3000.00,
    vatOnPurchases: 1600.00,
    netVAT: 1400.00,
    transactions: [
      { date: '2024-01-15', type: 'Sale', customer: 'Customer A', amount: 1200.00, vat: 240.00 },
      { date: '2024-01-18', type: 'Purchase', customer: 'Supplier B', amount: 600.00, vat: 120.00 },
      { date: '2024-01-22', type: 'Sale', customer: 'Customer C', amount: 1800.00, vat: 360.00 },
      { date: '2024-01-25', type: 'Purchase', customer: 'Supplier D', amount: 450.00, vat: 90.00 }
    ]
  };

  const canExport = user?.permissions?.reports?.includes('export');

  const handleExport = (format) => {
    alert(`${format.toUpperCase()} export functionality will be implemented with Google Sheets integration`);
  };

  const handleGenerateReport = () => {
    alert(`Generating ${reportType} report for ${period}`);
  };

  return (
    <Box>
		{/* Header */}
		<Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
		  <Box display="flex" alignItems="center" gap={2}>
			<IconButton 
			  onClick={() => navigate('/dashboard')}
			  color="primary"
			>
			  <ArrowBackIcon />
			</IconButton>
			<Typography variant="h4">Reports</Typography>
		  </Box>
		  <Box display="flex" gap={2}>
			{canExport && (
			  <>
				<Button
				  variant="outlined"
				  startIcon={<ExportIcon />}
				  onClick={() => handleExport('excel')}
				>
				  Export Excel
				</Button>
				<Button
				  variant="outlined"
				  startIcon={<PdfIcon />}
				  onClick={() => handleExport('pdf')}
				  color="error"
				>
				  Export PDF
				</Button>
			  </>
			)}
		  </Box>
		</Box>

      {/* Report Controls */}
      <Paper style={{ padding: '20px', marginBottom: '20px' }}>
        <Typography variant="h6" gutterBottom>Report Parameters</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Report Type</InputLabel>
              <Select
                value={reportType}
                label="Report Type"
                onChange={(e) => setReportType(e.target.value)}
              >
                <MenuItem value="vat-summary">VAT Summary</MenuItem>
                <MenuItem value="transaction-detail">Transaction Details</MenuItem>
                <MenuItem value="vat-return">VAT Return</MenuItem>
                <MenuItem value="customer-summary">Customer Summary</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Period</InputLabel>
              <Select
                value={period}
                label="Period"
                onChange={(e) => setPeriod(e.target.value)}
              >
                <MenuItem value="2024-01">January 2024</MenuItem>
                <MenuItem value="2024-02">February 2024</MenuItem>
                <MenuItem value="2024-03">March 2024</MenuItem>
                <MenuItem value="2024-Q1">Q1 2024</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="contained"
              onClick={handleGenerateReport}
              fullWidth
            >
              Generate Report
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* VAT Summary Report */}
      {reportType === 'vat-summary' && (
        <Grid container spacing={3}>
          {/* Summary Cards */}
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Sales
                    </Typography>
                    <Typography variant="h5" style={{ color: '#2e7d32' }}>
                      {Currency.format(vatSummaryData.totalSales)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Purchases
                    </Typography>
                    <Typography variant="h5" style={{ color: '#ed6c02' }}>
                      {Currency.format(vatSummaryData.totalPurchases)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      VAT on Sales
                    </Typography>
                    <Typography variant="h5" color="primary">
                      {Currency.format(vatSummaryData.vatOnSales)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Net VAT Due
                    </Typography>
                    <Typography variant="h5" style={{ color: '#d32f2f' }}>
                      {Currency.format(vatSummaryData.netVAT)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* VAT Calculation Breakdown */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  VAT Calculation
                </Typography>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>VAT on Sales:</Typography>
                  <Typography>{Currency.format(vatSummaryData.vatOnSales)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>VAT on Purchases:</Typography>
                  <Typography style={{ color: '#2e7d32' }}>
                    -{Currency.format(vatSummaryData.vatOnPurchases)}
                  </Typography>
                </Box>
                <hr style={{ margin: '10px 0' }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="h6">Net VAT Due:</Typography>
                  <Typography variant="h6" style={{ color: '#d32f2f' }}>
                    {Currency.format(vatSummaryData.netVAT)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Recent Transactions */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recent Transactions
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell align="right">VAT</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {vatSummaryData.transactions.map((transaction, index) => (
                        <TableRow key={index}>
                          <TableCell>{transaction.date}</TableCell>
                          <TableCell>
                            <Typography 
                              variant="body2" 
                              style={{ 
                                color: transaction.type === 'Sale' ? '#2e7d32' : '#ed6c02' 
                              }}
                            >
                              {transaction.type}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {Currency.format(transaction.amount)}
                          </TableCell>
                          <TableCell align="right">
                            {Currency.format(transaction.vat)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Other Report Types Placeholder */}
      {reportType !== 'vat-summary' && (
        <Card>
          <CardContent style={{ textAlign: 'center', padding: '40px' }}>
            <Typography variant="h6" gutterBottom>
              {reportType.replace('-', ' ').toUpperCase()} Report
            </Typography>
            <Typography color="textSecondary">
              This report type will be fully implemented with Google Sheets integration.
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: '10px' }}>
              Selected: {reportType} for period {period}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default Reports;